import { Invoice } from "@common/types/invoice";
import { useCallback, useMemo, useState } from "react";
import { EagerLoaded } from "@common/types/base/orm";
import { Loader } from "@design-system/Loader/Loader";
import { useInvoices, useUpdateInvoice } from "@api/invoice.api";
import { useScope } from "@state/scopeContext.lib";
import { useHasAnyAccrualLedger } from "@api/ledger.api";
import { Drawer } from "@design-system/Drawer/Drawer";
import { useOverlays } from "../../contexts/OverlayContext";
import { InvoiceLineItemsView } from "./views/invoiceLineItemsView/InvoiceLineItemsView";
import { createDefaultInvoiceLineItemsView } from "./views/defaultInvoiceViews";
import { InvoiceDetails } from "./InvoiceDetails";
import { useInvoiceDetailsScheduleCallbacks } from "./useInvoiceDetailsScheduleCallbacks";
import { ManualInvoiceEditDrawer } from "./ManualInvoiceEditDrawer/ManualInvoiceEditDrawer";
import styles from "./InvoicesDrillDownDrawer.module.scss";

interface InvoiceLineItemsDrilldownDrawerProps {
    open: boolean;
    onClose: () => void;
    invoiceId: string;
    entityIds: number[];
}

const InvoiceLineItemsDrilldownDrawerContent: React.FC<
    Pick<InvoiceLineItemsDrilldownDrawerProps, "invoiceId" | "entityIds">
> = ({ invoiceId, entityIds }) => {
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice>();
    const [editInvoice, setEditInvoice] = useState<Invoice | null>(null);
    const { workspaceId } = useScope();
    const updateInvoiceMutation = useUpdateInvoice();

    const handleUpdateMemo = useCallback(
        async (memo: string | null) => {
            if (!workspaceId || !selectedInvoice) {
                return;
            }
            await updateInvoiceMutation.mutateAsync({
                workspaceId,
                invoiceId: selectedInvoice.id,
                memo,
            });
        },
        [workspaceId, selectedInvoice, updateInvoiceMutation],
    );

    const { data, isLoading } = useInvoices({
        filters: { ids: [invoiceId], entityIds },
        page: 1,
        limit: 1,
    });

    const invoices = data?.data ?? null;
    // Callers without the invoice's entity (e.g. chat refs) pass every
    // workspace entity to find it; scope accrual columns to its own entity.
    const invoiceEntityIds = useMemo(
        () =>
            invoices
                ? Array.from(
                      new Set(invoices.map((invoice) => invoice.entityId)),
                  )
                : entityIds,
        [invoices, entityIds],
    );
    const hasAnyAccrualLedgers = useHasAnyAccrualLedger(invoiceEntityIds);
    const hasVisibleAccrualLedgerColumn = hasAnyAccrualLedgers;

    const invoiceLineItemsView = useMemo(
        () =>
            createDefaultInvoiceLineItemsView({
                hasAnyAccrualLedgers: hasVisibleAccrualLedgerColumn,
            }),
        [hasVisibleAccrualLedgerColumn],
    );

    const handleInvoiceClick = useCallback((invoice: Invoice) => {
        setSelectedInvoice(invoice);
    }, []);

    const { scheduleCallbacks, scheduleOverlays } =
        useInvoiceDetailsScheduleCallbacks(setSelectedInvoice);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className={styles.content}>
            <InvoiceLineItemsView
                view={invoiceLineItemsView}
                hasAnyAccrualLedgers={hasVisibleAccrualLedgerColumn}
                invoices={invoices}
                selectedInvoiceId={selectedInvoice?.id}
                onInvoiceClick={handleInvoiceClick}
            />

            {selectedInvoice && (
                <InvoiceDetails
                    showLeftBorder
                    invoice={selectedInvoice as EagerLoaded<Invoice, "lines">}
                    setInvoice={setSelectedInvoice}
                    onEdit={() => setEditInvoice(selectedInvoice)}
                    onUpdateMemo={handleUpdateMemo}
                    hideCloseButton={false}
                    hideShowLineItems
                    scheduleCallbacks={scheduleCallbacks}
                />
            )}

            {scheduleOverlays}

            {editInvoice && (
                <ManualInvoiceEditDrawer
                    invoices={[editInvoice]}
                    open
                    onClose={() => {
                        setEditInvoice(null);
                        setSelectedInvoice(undefined);
                    }}
                />
            )}
        </div>
    );
};

const InvoiceLineItemsDrilldownDrawer: React.FC<
    InvoiceLineItemsDrilldownDrawerProps
> = ({ open, onClose, invoiceId, entityIds }) => {
    const [isOpen, setIsOpen] = useState(false);

    const shouldShowContent = isOpen || open;

    return (
        <Drawer
            open={open}
            onClose={onClose}
            direction="right"
            onAnimationEnd={setIsOpen}
            headerAddonLeft="Invoice"
            noPadding
        >
            {shouldShowContent && (
                <InvoiceLineItemsDrilldownDrawerContent
                    invoiceId={invoiceId}
                    entityIds={entityIds}
                />
            )}
        </Drawer>
    );
};

export const InvoiceLineItemsDrilldownDrawerWrapper: React.FC = () => {
    const { getOverlayInfo, closeOverlay, overlayIsClosing } = useOverlays();
    const overlayInfo = getOverlayInfo<"invoiceLineItemsDrilldown">(
        "invoiceLineItemsDrilldown",
    );

    if (!overlayInfo?.data) {
        return null;
    }

    return (
        <InvoiceLineItemsDrilldownDrawer
            open={!overlayIsClosing}
            onClose={closeOverlay}
            invoiceId={overlayInfo.data.invoiceId}
            entityIds={overlayInfo.data.entityIds}
        />
    );
};
